// YOUR APPS — the second of the welcome's three choices on the setup surface
// (components/OnboardingWizard.js; functions/onboardingPresets.js; wt/presets
// CONTRACT §1: "Every step that writes shows its own line, and every failure
// is a line with a retry") — and those lines: OnboardingSetupLines, also on
// the done step and in the conversation.
//
// The app rows are the wizard's own (OnboardingAppTile: the calm glyph, the
// name, the plain tick once picked). Every value comes from
// functions/onboardingPresets.js through `el.call`; every factory names the
// root keys it follows (`…Deps`).

// YOUR APPS — before the apply is sent: the apps as toggle rows (two columns
// wide enough for a whole name, one on a phone), their count, the apps the
// catalog does not carry yet counted under the grid, and a quiet link to find
// more in the App Store. Once sent: the apply's own lines.
export const OnboardingStackStep = {
  extends: 'OnboardingWizardStep',
  wizardStep: 'stack',

  StackBar: {
    display: 'flex',
    alignItems: 'center',
    minWidth: '0',
    hideDeps: [
      (el, s) => s.root && s.root._onboardingFlow,
      (el, s) => s.root && s.root._wsAppsCatalog
    ],
    hide: (el, s) => {
      const flow = (s.root && s.root._onboardingFlow) || {}
      return !!flow.stackSent || !(el.call('onboardingStackView', s.root) || {}).total
    },
    StackCount: {
      tag: 'span',
      fontSize: 'Z1',
      color: 'inactive',
      textDeps: [
        (el, s) => s.root && s.root._onboardingFlow,
        (el, s) => s.root && s.root._wsAppsCatalog
      ],
      text: (el, s) => (el.call('onboardingStackView', s.root) || {}).countText || ''
    }
  },

  StackNote: {
    tag: 'p',
    role: 'status',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'inactive',
    hideDeps: [
      (el, s) => s.root && s.root._onboardingFlow,
      (el, s) => s.root && s.root._wsAppsCatalogLoaded
    ],
    hide: (el, s) => {
      const flow = (s.root && s.root._onboardingFlow) || {}
      const view = el.call('onboardingStackView', s.root) || {}
      return !!flow.stackSent || !!view.total || (view.ready && !view.custom)
    },
    textDeps: [(el, s) => s.root && s.root._wsAppsCatalogLoaded],
    text: (el, s) =>
      el.call(
        'polyglot',
        (el.call('onboardingStackView', s.root) || {}).ready
          ? 'workspace.onboarding.stack.none'
          : 'workspace.onboarding.stack.loading'
      )
  },

  OnboardingTileGrid: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-F2)), 1fr))',
    hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
    hide: (el, s) => !!((s.root && s.root._onboardingFlow) || {}).stackSent,
    childExtends: 'OnboardingStackTile',
    childrenDeps: [
      (el, s) => s.root && s.root._onboardingFlow,
      (el, s) => s.root && s.root._wsAppsCatalog,
      (el, s) => s.root && s.root.workspace
    ],
    children: (el, s) => (el.call('onboardingStackView', s.root) || {}).rows || []
  },

  StackSoon: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'inactive',
    hideDeps: [
      (el, s) => s.root && s.root._onboardingFlow,
      (el, s) => s.root && s.root._wsAppsCatalog
    ],
    hide: (el, s) => {
      const flow = (s.root && s.root._onboardingFlow) || {}
      return !!flow.stackSent || !(el.call('onboardingStackView', s.root) || {}).soon
    },
    textDeps: [(el, s) => s.root && s.root._wsAppsCatalog],
    text: (el, s) => (el.call('onboardingStackView', s.root) || {}).soonText || ''
  },

  // the quiet way to more apps — a preset's or your own (it ends the welcome:
  // what is picked here is sent first)
  AppCapsuleBtnGhost: {
    compact: true,
    alignSelf: 'flex-start',
    hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
    hide: (el, s) => !!((s.root && s.root._onboardingFlow) || {}).stackSent,
    text: '{{ workspace.onboarding.stack.browse | polyglot }}',
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('onboardingWizardFinish', 'store')
    }
  },

  OnboardingSetupLines: {}
}

// ONE APP of Your apps — the wizard's app row (its calm glyph, its name, the
// plain tick once picked) with its mark: Installed (fixed — the apply only
// adds) or Suggested. A press ticks or unticks it.
export const OnboardingStackTile = {
  extends: 'OnboardingAppTile',
  ariaPressedDeps: [(el, s) => s.root && s.root._onboardingFlow],
  ariaPressed: (el, s) => (s && s.picked ? 'true' : 'false'),
  ariaDisabled: (el, s) => (s && !s.toggleable ? 'true' : null),
  '&[aria-disabled="true"]': { cursor: 'default' },
  '&[aria-disabled="true"]:hover': { background: 'selected' },
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (s && s.toggleable) el.call('onboardingStackToggle', s.id)
  },
  // The app's one line from its listing, under its name (persona round 3,
  // W3: elena — "22 app tiles show one word only, no description"); its mark
  // (Installed / Suggested) after it.
  TileWords: {
    TileHint: { order: '2' },
    TileDescr: {
      tag: 'span',
      order: '1',
      hide: (el, s) => !(s && s.descr),
      fontSize: 'Z',
      lineHeight: '1.45',
      color: 'caption',
      overflowWrap: 'break-word',
      text: (el, s) => (s && s.descr) || ''
    }
  }
}

// THE LINES of the flow's apply — the result line on top (Setting up, then
// what was set up), one line per app (its glyph, its name, Installing… /
// Installed / Already there / Coming soon / Could not install), the apps with
// no name counted, the channels and folders line, and Try again whenever a
// line failed. A live region: the answer is read out when it lands.
export const OnboardingSetupLines = {
  tag: 'section',
  ariaLive: 'polite',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  minWidth: '0',
  hideDeps: [
    (el, s) => s.root && s.root._onboardingFlow,
    (el, s) => s.root && s.root._presetApply,
    (el, s) => s.root && s.root._wsAppsCatalog
  ],
  hide: (el, s) => !(el.call('onboardingSetupView', s.root) || {}).show,

  SetupTitle: {
    tag: 'h3',
    margin: '0',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    textDeps: [
      (el, s) => s.root && s.root._presetApply,
      (el, s) => s.root && s.root._onboardingFlow
    ],
    text: (el, s) => (el.call('onboardingSetupView', s.root) || {}).title || ''
  },

  OnboardingLineList: {
    childrenDeps: [
      (el, s) => s.root && s.root._presetApply,
      (el, s) => s.root && s.root._wsAppsCatalog
    ],
    children: (el, s) => (el.call('onboardingSetupView', s.root) || {}).lines || []
  },

  SetupSoon: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'inactive',
    hideDeps: [(el, s) => s.root && s.root._presetApply],
    hide: (el, s) => !(el.call('onboardingSetupView', s.root) || {}).soonText,
    textDeps: [(el, s) => s.root && s.root._presetApply],
    text: (el, s) => (el.call('onboardingSetupView', s.root) || {}).soonText || ''
  },

  SetupMade: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'inactive',
    hideDeps: [(el, s) => s.root && s.root._presetApply],
    hide: (el, s) => !(el.call('onboardingSetupView', s.root) || {}).setupText,
    textDeps: [(el, s) => s.root && s.root._presetApply],
    text: (el, s) => (el.call('onboardingSetupView', s.root) || {}).setupText || ''
  },

  SetupError: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'dangerInk',
    hideDeps: [(el, s) => s.root && s.root._presetApply],
    hide: (el, s) => !(el.call('onboardingSetupView', s.root) || {}).errorText,
    textDeps: [(el, s) => s.root && s.root._presetApply],
    text: (el, s) => (el.call('onboardingSetupView', s.root) || {}).errorText || ''
  },

  AppCapsuleBtnGhost: {
    compact: true,
    alignSelf: 'flex-start',
    hideDeps: [(el, s) => s.root && s.root._presetApply],
    hide: (el, s) => !(el.call('onboardingSetupView', s.root) || {}).retryable,
    text: '{{ marketplace.preset.tryAgain | polyglot }}',
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('onboardingStackRetry')
    }
  }
}

// A list of setup lines — as many columns as fit, never narrower than F2 (a
// whole app name fits a line). The apply's own list (OnboardingSetupLines) and
// Fly's ready screen (components/OnboardingReadyStep.js) are both this list,
// each feeding it its rows.
export const OnboardingLineList = {
  tag: 'ul',
  margin: '0',
  padding: '0',
  listStyle: 'none',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-F2)), 1fr))',
  gap: 'Y Z',
  minWidth: '0',
  childExtends: 'OnboardingSetupLine',
  childrenAs: 'state'
}

// One line of the apply: the app's glyph and name, then what happened to it.
// A done app wears the plain tick; a failure reads in the danger ink (Try
// again is the block's).
export const OnboardingSetupLine = {
  tag: 'li',
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  minWidth: '0',
  minHeight: 'controlHCompact',
  OnboardingAppGlyph: {},
  LineName: {
    tag: 'span',
    flex: '1 1 auto',
    minWidth: '0',
    fontSize: 'Z2',
    fontWeight: '500',
    lineHeight: '1.3',
    color: 'title',
    overflowWrap: 'anywhere',
    text: (el, s) => (s && s.title) || ''
  },
  LineState: {
    tag: 'span',
    display: 'flex',
    alignItems: 'center',
    gap: 'X',
    flexShrink: '0',
    fontSize: 'Z1',
    lineHeight: '1.3',
    color: (el, s) => (s && s.failed ? 'dangerInk' : 'caption'),
    Icon: {
      name: 'checkmark',
      boxSize: 'iconXs',
      hide: (el, s) => !(s && s.done)
    },
    LineStateText: {
      tag: 'span',
      text: (el, s) => (s && s.stateKey ? el.call('polyglot', s.stateKey) : '')
    }
  }
}
